/**
 * Modelo de Dominio: Cumplimiento de Servicio
 * 
 * Migrado desde:
 * - FilterOrdenesProgViewImpl.java (listado de programaciones pendientes)
 * - CreateOrUpdateCumplimientoViewImpl.java (formulario de cumplimiento)
 * 
 * Tablas: TPSC_CUMPLIM_SERVICIOS, TPSC_PROGRAMAC_SERVICIOS
 * 
 * @author Emerson Rodriguez - Ingeneo
 */

// === Paginación ===

export interface CursorPageResponse<T> {
  content: T[];
  size: number;
  nextCursor: string | null;
  previousCursor: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
  totalElements?: number;
}

// === Filtros de Búsqueda ===

/**
 * Filtros para buscar programaciones pendientes de cumplimiento.
 * 
 * Legacy: FilterOrdenesProgViewImpl.java -> campos de filtro
 */
export interface CumplimientoFiltro {
  /** Número de orden de servicio - Legacy: EQUAL, no LIKE */
  ordenServicio?: string;
  
  /** Nmconsecutivo de oficina - Legacy: nmconsecutivo_oficina.nmconsecutivo */
  cdoficina?: string;
  
  /** Código de unidad administrativa - Legacy: cdunidad_administrativa (STRING, no nmconsecutivo) */
  cdunidadAdministrativa?: string;
  
  /** Nmconsecutivo del cliente - Legacy: nmconsecutivo_cliente.nmconsecutivo */
  nmconsecutivoCliente?: number;
  
  /** Tipo documento del cliente (no usado en legacy) */
  tipoDocumentoCliente?: string;
  
  /** Nmconsecutivo del asesor/empleado - Legacy: nmconsecutivo_asesor.nmconsecutivo */
  nmconsecutivoAsesor?: number;
  
  /** CDPERSONA del asesor - Para búsqueda desde dialog (TPSC_EMPLEADOS.CDPERSONA) */
  cdpersonaAsesor?: number;
  
  /** Tipo documento del asesor (no usado en legacy) */
  tipoDocumentoAsesor?: string;
  
  /** Nmconsecutivo del beneficiario - Legacy: nmconsecutivo_beneficiario.nmconsecutivo */
  nmconsecutivoBeneficiario?: number;
  
  /** Tipo documento del beneficiario (no usado en legacy) */
  tipoDocumentoBeneficiario?: string;
  
  /** Fecha inicial del rango - Legacy: Trunc(feprogramacion) >= */
  fechaInicial?: string;
  
  /** Fecha final del rango - Legacy: Trunc(feprogramacion) <= */
  fechaFinal?: string;
  
  /** Nmconsecutivo de homologacion/tarea - Legacy: nmconsec_homologacion_servicio.nmconsecutivo */
  nmconsecutivoHomologacion?: number;
  
  /** Nmconsecutivo del centro de trabajo - Legacy: nmconsec_benef_ct.nmconsecutivo */
  nmconsecutivoCentroTrabajo?: number;
}


// === Riesgo Potencial ===

/**
 * Filtros para consulta de riesgos potenciales.
 *
 * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales/conteo
 */
export interface RiesgoPotencialFiltro {
  /** Consecutivo del riesgo. Criterio dominante: descarta el resto (legacy). */
  nmconsecutivo?: number;
  /** Código de la orientación de servicio. Criterio dominante (legacy: tbOrden). */
  dsorden?: string;
  nmconsecutivoOficina?: number;
  nmconsecutivoAsesor?: number;
  nmconsecutivoBenef?: number;
  nmconsecutivoCumpl?: number;
  /** Agente de riesgo — TPSC_AGENTE_RIESGO (legacy: lbTipoRiesgo). */
  nmconsecRiesgo?: number;
  /** Tipo de situación — columna distinta de nmconsecRiesgo. */
  nmtipoSituacion?: number;
  /** DNI del profesional de prevención (legacy: tbProfesional). */
  dniProfesional?: string;
  cdestado?: string;
  /** Conjunto de estados (legacy: listEstados). */
  cdestados?: string[];
  dniBeneficiario?: string;
  fechaIdentificacionInicial?: string;
  fechaIdentificacionFinal?: string;
  fechaSeguimientoInicial?: string;
  fechaSeguimientoFinal?: string;
}

/**
 * Agregado de conteo de riesgos potenciales.
 */
export interface RiesgoPotencialConteo {
  total: number;
  abiertos: number;
  cerrados: number;
}

/**
 * Página con paginación keyset/cursor (distinta de CursorPageResponse porque el
 * cursor viaja como número, alineado con NMCONSECUTIVO del riesgo).
 *
 * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales
 */
export interface RiesgoPotencialPage {
  content: RiesgoPotencialListadoItem[];
  size: number;
  nextCursor: string | null;
  previousCursor: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
  totalElements?: number;
}

/** Dirección de paginación keyset. */
export type CursorDireccion = 'NEXT' | 'PREVIOUS';

/**
 * Fila del listado de riesgos potenciales.
 *
 * Legacy: RiesgoPotencialViewImpl.java -> tabla stRiesgoPotencial
 */
export interface RiesgoPotencialListadoItem {
  nmconsecutivo: number;
  /** Orientación de servicio — legacy: columna «Orientación de servicio» */
  dsorden?: string;
  nmconsecutivoOficina?: number;
  /** Descripción de la oficina — legacy: columna «Oficina» */
  dsoficina?: string;
  /** Contrato/póliza — legacy: columna «Contrato» */
  cdcontrato?: string;
  /** Razón social del beneficiario — legacy: columna «Empresa» */
  dsnombreEmpresa?: string;
  /**
   * Fecha de identificación de negocio: fecha de programación cuando el riesgo viene
   * de un cumplimiento, fecha de ingreso cuando es «sin OC» (legacy).
   */
  feidentificacion?: string;
  nmconsecRiesgo?: number;
  /** Descripción del agente de riesgo — legacy: columna «Riesgo identificado» */
  dsriesgo?: string;
  nmconsecControl?: number;
  /** Descripción del control — legacy: columna «Tipo de control» */
  dscontrol?: string;
  cdestado: string;
  /** Descripción del estado — legacy: columna «Estado» (dsestado, no el código) */
  dsestado?: string;
  dssituacion?: string;
  dsconsecuencia?: string;
  nmtipoSituacion?: number;
  nmprioridad?: number;
  /** Fecha y hora de registro (auditoría) */
  feingresa?: string;
  feseguimiento?: string;
  nmconsecutivoAsesor?: number;
  dsnombreAsesor?: string;
  nmconsecutivoBenef?: number;
  nmconsecutivoCumpl?: number;
  cdevidenciap8?: string;
}

/**
 * Detalle completo de un riesgo potencial.
 *
 * Legacy: SeguimientoRiesgoPotViewImpl.java (panel "Detalle Riesgo")
 * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}
 */
export interface RiesgoPotencialDetalle {
  nmconsecutivo: number;
  cdestado: string;
  /** Descripción del estado (TPSC_ESTADOS.DSESTADO) */
  dsestado?: string;
  dssituacion?: string;
  dsconsecuencia?: string;
  nmpersonasexp?: number;
  nmtipoSituacion?: number;
  nmprioridad?: number;
  dssede?: string;
  dsarea?: string;
  dsproceso?: string;
  dsdetubicacion?: string;
  dsrecomendacion?: string;
  feseguimiento?: string;
  snasesorExpuesto?: string;
  snsolucionSitio?: string;
  nmconsecutivoCumpl?: number;
  nmconsecRiesgo?: number;
  /** «Riesgo que le aplica» (TPSC_AGENTE_RIESGO.DSRIESGO) */
  dsriesgo?: string;
  nmconsecControl?: number;
  /** «Tipo de control» (TPSC_CONTROL_RIESGO.DSCONTROL) */
  dscontrol?: string;
  nmconsecutivoOficina?: number;
  dsoficina?: string;
  nmconsecutivoAsesor?: number;
  dsnombreAsesor?: string;
  nmconsecutivoBenef?: number;
  cdcontrato?: string;
  dsnombreEmpresa?: string;
  nmconsecProfesional?: number;
  dsorden?: string;
  feidentificacion?: string;
  cdevidenciap8?: string;
  feingresa?: string;
  dniIngresa?: string;
}

/**
 * Campos editables del diálogo «Modificar Detalle».
 *
 * Legacy: SeguimientoRiesgoPotViewImpl.onClickAceptar() → comando guardarRiesgoPot
 * Backend: PUT /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}
 *
 * Asesor y profesional sólo aplican cuando el riesgo no proviene de un cumplimiento.
 */
export interface ActualizarRiesgoPotencialRequest {
  dssituacion: string;
  dsrecomendacion: string;
  nmconsecRiesgo: number;
  nmconsecutivoAsesor?: number;
  nmconsecProfesional?: number;
}

/** Estados de riesgo potencial - Legacy: TpscEstados / RiesgoCumplimiento.java */
export const ESTADO_RIESGO = {
  REPORTADO: 'RPRP',
  CERRADO: 'RPCR',
  ESCALADO_N1: 'RPE1',
  ESCALADO_N2: 'RPE2',
  ESCALADO_N3: 'RPE3',
  CERRADO_NO_APLICA: 'RPCA',
  CERRADO_POR_DESAFILIACION: 'RPCD',
  REABIERTO: 'RPRA'
} as const;

/** Estados que solo permiten "Reapertura" (formulario oculto) - Legacy: reset(dto) */
export const ESTADOS_SOLO_REAPERTURA: string[] = [
  ESTADO_RIESGO.CERRADO,
  ESTADO_RIESGO.ESCALADO_N3,
  ESTADO_RIESGO.CERRADO_NO_APLICA,
  ESTADO_RIESGO.CERRADO_POR_DESAFILIACION
];

/**
 * Un registro del histórico de seguimientos de un riesgo potencial.
 *
 * Legacy: Tpsc_seg_riesgo_cumpdto / TPSC_SEG_RIESGO_CUMP
 * Backend: GET /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/seguimientos
 */
export interface RiesgoSeguimiento {
  nmconsecutivo: number;
  nmconsecutivoRiesgo: number;
  dsobservacion?: string;
  dsrecomendacion?: string;
  feseguimiento?: string;
  snaccionSufic?: string;
  sncerradoApl?: string;
  sncerradoAfilia?: string;
  snaplicaRecomend?: string;
  nmconsecControl?: number;
  /** Descripción del control (TPSC_CONTROL_RIESGO.DSCONTROL) */
  dscontrol?: string;
  /** Id del documento de evidencia en P8 */
  cddocumento?: string;
  cdestado: string;
  /** Descripción del estado (TPSC_ESTADOS.DSESTADO) */
  dsestado?: string;
  feingresa?: string;
  dniIngresa?: string;
}

/**
 * Respuesta de la carga de evidencia P8 para un seguimiento de riesgo potencial.
 *
 * Backend: POST /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/evidencia
 */
export interface EvidenciaSeguimientoResponse {
  idDocumento: string;
}

/**
 * Payload para registrar un seguimiento (o una reapertura) de riesgo potencial.
 *
 * Legacy: SeguimientoRiesgoPotViewImpl.getDto() / onClickReapertura()
 * Backend: POST /api/servicios/orientacionesProgramadas/riesgos-potenciales/{idRiesgo}/seguimientos
 */
export interface GuardarRiesgoSeguimientoRequest {
  dsobservacion?: string;
  dsrecomendacion?: string;
  feseguimiento?: string;
  snaccionSufic?: string;
  sncerradoApl?: string;
  sncerradoAfilia?: string;
  snaplicaRecomend?: string;
  nmconsecControl?: number;
  cddocumento?: string;
  cdestado?: string;
  // El correo del profesional de prevención lo resuelve el servidor a partir del
  // riesgo; no viaja en el payload para que el cliente no dirija la notificación.
}
// === Programación Pendiente ===

/**
 * Programación pendiente de cumplimiento.
 * 
 * Representa una fila en la tabla de programaciones pendientes.
 * Legacy: FilterOrdenesProgViewImpl.java -> stProgramaciones (tabla)
 */
export interface ProgramacionPendiente {
  /** Consecutivo de la programación */
  nmconsecutivo: number;
  
  /** Consecutivo del detalle de orden de servicio */
  nmconsecOrdenServDetalle: number;
  
  /** Número de orden de servicio */
  nmconsecutivoManualOrden: string;
  
  /** Tipo de servicio (CAMPO, VIRTUAL, etc.) */
  dstipo?: string;
  
  /** Código del tipo de cita */
  nmconsecutivoTipoCita?: number;
  
  /** Descripción del tipo de cita */
  dstipoCita?: string;
  
  /** Nombre de la aseguradora (columna "Aseguradora" en legacy) */
  dsnombreAseguradora?: string;
  
  /** Nombre del cliente/beneficiario de la orden (columna "Cliente" en legacy) */
  dsnombreCliente?: string;
  
  /** Nombre del beneficiario de la programación */
  dsnombreBeneficiario?: string;
  
  /** Consecutivo del beneficiario */
  nmconsecutivoBeneficiario?: number;
  
  /** DNI del beneficiario, formato "TIPO+NUMERO" (ej: "CC12345678")
   * El tipo se extrae como prefijo (CC, NIT, CE, PP, etc.)
   * Legacy: dto.getNmconsecutivo_beneficiario().getCdpersona().getDni()
   */
  dniBeneficiario?: string;

  /** Tipo de documento del beneficiario (legacy: cdtipo_identificacion) */
  tipoDocumentoBeneficiario?: string;

  /** Numero de documento del beneficiario (legacy: nmidentificacion) */
  numeroDocumentoBeneficiario?: string;
    /** Nombre del contacto de la programación
   * Legacy: dto.getNmconsecutivo_contacto().getDsnombre()
   */
  dsnombreContacto?: string;
  
  /** Email del contacto de la programación
   * Legacy: dto.getNmconsecutivo_contacto().getDsemail()
   */
  dsemailContacto?: string;
    /** Nombre del centro de trabajo */
  dscentroTrabajo?: string;
  
  /** Consecutivo del beneficiario-centro de trabajo */
  nmconsecBenefCt?: number;
  
  /** Código de la tarea */
  cdtarea?: string;
  
  /** Nombre de la tarea (TPSC_CLIENTES_SERVICIOS.DSSERVICIO)
   * Legacy: slNombreTarea.setText(dto.getNmconsec_servicio_cliente().getDsservicio())
   */
  dstarea?: string;
  
  /** Nombre del servicio (TPSC_SERVICIOS.DSNOMBRE_SERVICIO)
   * Legacy: slNombreServicio.setText(dto.getNmconsecutivo_servicio().getDsnombre_servicio())
   */
  dsnombreServicio?: string;
  
  /** Tipo de manejo de la unidad de medida del servicio.
   * Legacy: servicio.unidadMedida.dstipomanejo
   * Valores: "T" = Tiempo, otros = Cantidad
   * Usado para determinar si cantidadEjecutada es editable en tipo INFORME.
   */
  dstipomanejo?: string;
  
  /** Fecha de programación (ISO date) */
  feprogramacion?: string;
  
  /** Hora inicial (HH:mm) */
  horaInicial?: string;
  
  /** Duración en horas */
  nmhorasCita?: number;
  
  /** Cantidad programada */
  nmcantidadProgramada?: number;
  
  /** Saldo por ejecutar */
  saldoPorEjecutar?: number;
  
  /** Código del estado */
  cdestado?: string;
  
  /** Descripción del estado */
  dsestado?: string;
  
  // ===== Municipios de Transporte =====
  // Legacy: vienen de TPSC_ORDEN_SERVICIO_DETAL.CDMUNICIPIO_ORIGEN/CDMUNICIPIO_ENTREGA
  
  /** Código del municipio de origen */
  cdmunicipioOrigen?: string;
  
  /** Nombre del municipio de origen */
  dsmunicipioOrigen?: string;
  
  /** Código SURA del municipio de origen (para cálculo de tarifas) */
  cdsuraOrigen?: string;
  
  /** Código del municipio de entrega */
  cdmunicipioEntrega?: string;
  
  /** Nombre del municipio de entrega */
  dsmunicipioEntrega?: string;
  
  /** Código SURA del municipio de entrega (para cálculo de tarifas) */
  cdsuraEntrega?: string;
  
  /** Consecutivo de la oficina de la orden. */
  nmconsecutivoOficina?: number;

  /** Consecutivo del cliente de la orden. */
  nmconsecutivoCliente?: number;

  /**
   * Consecutivo del asesor de la cita. Para citas complementarias (sin orden)
   * el backend resuelve desde él la oficina y el cliente del respaldo de tarifa.
   * Legacy: CrearCitaAdministrativaActivity.fulFillment().
   */
  nmconsecutivoAsesor?: number;

  // ===== Población y Transporte =====
  
  /** Código de población para tarifa de transporte */
  cdpoblacion?: string;
  
  /** Nombre de la población */
  dspoblacion?: string;
  
  /** Valor del transporte (precalculado desde tarifa) */
  ptvalorTransporte?: number;
  
  /** Pago transporte a empleado S/N */
  snpagoTransporteEmpleado?: string; 

  /** S/N programación automática (legacy: SNPROGRAMACION_AUTOMATICA) */
  snprogramacionAutomatica?: string;

  /** S/N OC automática (legacy: SNOCAUTOMATICA) */
  snocautomatica?: string;

  /**
   * Semantic Sin OC flag for no-order fulfillment flows.
   * Prevents relying only on legacy snocautomatica markers.
   */
  cumplimientoSinOc?: boolean;

  /** Código de clasificación del servicio (legacy: CDCLASIFICACION) */
  cdclasificacion?: string;

  /** Código de persona del beneficiario (TPSC_PERSONAS.CDPERSONA) */
  cdpersona?: number;

  /** Clasificación/descripción del servicio (ej: 'ASESOR...', 'CAPACITACI...') - HU1132419 */
  dsclasificacion?: string;

  /** Contrato del beneficiario (para verificar si está exento de acta) - HU1132419 */
  cdcontratoBeneficiario?: string;

  /** S/N identifica riesgo potencial (legacy: SNIDENTIFICA_RIESGO) - HU1132419 */
  snidentificaRiesgo?: string;

  /** S/N atención virtual (legacy: SNATENCION_VIRTUAL) - HU1132419 */
  snatencionVirtual?: string;
}

// === Request para Registrar Cumplimiento ===

/**
 * Request para crear un nuevo cumplimiento.
 * 
 * Legacy: CreateOrUpdateCumplimientoViewImpl.java -> botón Grabar
 */
export interface CumplimientoRequest {
  /** Consecutivo de la programación a cumplir */
  nmconsecutivoProgramacion?: number;

  /** Consecutivo del detalle de orden (flujo de orientaciones no programadas) */
  nmconsecOrdenServDetalle?: number;
  
  /** Fecha del cumplimiento (ISO date) */
  fechaCumplimiento: string;
  /** Fecha de programación (ISO date) - solo para orientaciones no programadas */
  fechaProgramacion?: string;
  
  /** Cantidad ejecutada */
  cantidadEjecutada: number;
  
  /** Número de asistentes */
  numeroAsistentes?: number;
  
  /** Nombre del contacto */
  nombreContacto?: string;
  
  /** Correo del contacto */
  correoContacto?: string;
  /** Contacto de la programación creada sin programación previa (legacy: nmconsecutivo_contacto). */
  nmconsecutivoContacto?: number;
  
  /** Hora de la cita (HH:mm) */
  horaCita?: string;
  
  /** Duración de la cita */
  duracion?: number;

  /**
   * Cantidad programada original del flujo Sin Orientación.
   * Se usa para crear la programación automática sin confundirla
   * con la cantidad ejecutada del cumplimiento.
   */
  cantidadProgramadaSinOc?: number;
  /** Código del tipo de cita */
  cdtipoCita?: number;
  
  /** Observaciones del cumplimiento */
  observaciones?: string;

  /**
   * Observación que se incluye en el correo al contacto. Solo aplica al flujo Sin OC de línea de atención
   * (forma de entrega distinta de DOMICILIO y SKYPE_EMPRESARIAL) con tipo de cita de campo.
   * Legacy: Cumplimiento.dsObsNotificacion (taObsNotifica). El backend la valida con @SafeText.
   */
  observacionNotificacion?: string;
  
  /** Recomendaciones */
  recomendaciones?: string;
  
  // === Transporte (visible si tipo CAMPO) ===
  
  /** ¿Pago de transporte al empleado? (S/N) */
  snpagoTransporteEmpleado?: string;
  
  /** ¿Atención virtual? (S/N) */
  snatencionVirtual?: string;
  
  /** Código municipio origen */
  cdmunicipioOrigen?: string;
  
  /** Código municipio entrega/destino */
  cdmunicipioEntrega?: string;
  
  /** Código de población */
  cdpoblacion?: string;
  
  /** Valor del transporte */
  ptvalorTransporte?: number;
  
  /** ¿Requiere transporte adicional? (S/N) */
  snrequiereTransporteAdicional?: string;
  
  /** Valor transporte adicional */
  ptvalorTransporteAdicional?: number;
  
  /** Observaciones del transporte adicional */
  observacionesTransporteAdicional?: string;
  
  /** Observaciones del transporte adicional (alias para DB) */
  dsobservacionesTransporteAdicional?: string;
  
  // === Riesgo Potencial (solo tipo CAMPO) ===
  
  /** ¿Identifica alertas tempranas de riesgo potencial? (S/N) */
  snidentificaRiesgo?: string;
  
  // === Observaciones/Recomendaciones del Informe (solo tipo INFORME) ===
  
  /** Observaciones específicas del informe (alias frontend) */
  observacionesInforme?: string;
  
  /** Recomendaciones del informe (alias frontend) */
  recomendacionesInforme?: string;
  
  // === Archivo del Informe (solo tipo INFORME) ===
  
  /** Nombre del archivo */
  archivoNombre?: string;
  
  /** Contenido del archivo en Base64 */
  archivoContenido?: string;
  
  /** Content-Type del archivo */
  archivoContentType?: string;
  
  /** Tipo de informe: P=Parcial, F=Final */
  tipoInforme?: string;
  
  /** ¿Enviar mail de aprobación? (true/false) */
  snmailAprobacion?: boolean;
  
  // === Asesor (flujo orientaciones no programadas) ===
  
  /**
   * DNI del asesor que realiza la orientación.
   * Solo aplica para flujo NO_PROGRAMADA.
   * Formato: LetraTipoId + Número (ej: "C12345678").
   * Las órdenes no programadas no tienen asesor en la BD; el usuario lo selecciona en el formulario.
   * Se usa como fallback cuando nmconsecutivoAsesor no está disponible.
   */
  dniAsesor?: string;

  /**
   * NMCONSECUTIVO del asesor (TPSC_EMPLEADOS) que realiza la orientación.
   * Solo aplica para flujo NO_PROGRAMADA. Se debe enviar SIEMPRE que se conozca
   * (TPSC_ORDEN_SERVICIO_DETAL.NMCONSECUTIVO_ASESOR_SUGERIDO), en lugar de solo el DNI,
   * para evitar una segunda búsqueda por DNI en agenda-ms que podría resolver un asesor
   * distinto al sugerido y omitir la validación de choque de citas.
   * Legacy: programacionDto.setNmconsecutivo_asesor(ordenDto.getNmconsecutivo_asesor_sugerido()).
   */
  nmconsecutivoAsesor?: number;
  
  // === Justificación (cumplimiento tardío) ===
  
  /** Código de justificación */
  cdjustificacion?: number;
  
  /** Observaciones de justificación */
  observacionesJustificacion?: string;
  
  // === Contactos Adicionales (hasta 3) - Legacy: Tpsc_cumpli_contactosdto ===
  
  /** Lista de contactos adicionales para notificar */
  contactosAdicionales?: ContactoAdicional[];

  // === Acta 360 (HU1132419 - tipoCita CAMPO no exento) ===

  /** Datos del acta a generar en sistema 360 */
  datosActa?: DatosActaRequest;

  /** Tipo de acta: 'ASESOR' | 'CAPACITACI' */
  tipoActa?: string | null;

  // === Sin Orientación de Servicio (HU1132420) ===

  /**
   * Indica que el cumplimiento se graba desde el flujo "Sin Orientación de
   * Servicio" - la OC todavía NO existe en BD. El backend creará la OC vía
   * SP_CREAR_OC_CUMPLIMIENTO antes de registrar el cumplimiento.
   *
   * Legacy: CumplimientoFacade.saveOrUpdate cuando isCumplimientoSinOc().
   */
  cumplimientoSinOc?: boolean;

  /**
   * Datos hidratados de la OC a crear cuando {@link cumplimientoSinOc} es true.
   * Provienen del endpoint POST /api/servicios/sin-oc/preparar-ingreso-cumplimiento.
   */
  datosSinOc?: DatosSinOcCumplimiento;
}

/**
 * Datos hidratados de la "orden de servicio detalle" usados para crear la OC
 * cuando se registra un cumplimiento desde el flujo Sin Orientación de Servicio.
 *
 * Espejo de {@code DatosSinOcCumplimiento.java} del dominio backend.
 */
export interface DatosSinOcCumplimiento {
  nmconsecHomologacion: number;
  /** TPSC_EMPLEADOS.NMCONSECUTIVO del asesor (no CDPERSONA). */
  nmconsecAsesor: number;
  nmconsecOficina: number;
  cdcontrato?: string;
  cdcentroTrabajo?: string;
  /** Municipios de la OC del formulario Sin OC (legacy: buscadorMunOrigen/buscadorMunEntrega). */
  cdmunicipioOrigen?: string;
  cdmunicipioEntrega?: string;
  cdproducto?: string;
  dscanalServicio?: string;
  cdformaEntrega?: string;
  nmexpediente?: string;
  dsestadoInvestigacion?: string;
}

/**
 * Payload del acta para enviar al backend (HU1132419).
 * Espejo de DatosCumplimientoActa.java del dominio backend.
 */
export interface DatosActaRequest {
  descripcionServicioPrestado: string;
  compromisosEmpresaAfiliada?: string;
  compromisosSura?: string;
  relacionDocumentacion?: string;
  fuente?: string;
  idCumplimiento?: string;
  requierePlanAccion?: string;
  canalAtencion?: string;
  envioNotificacion?: string;
  numeroAsistentes?: number;
  asistentes?: { nombre: string; cargo: string }[];
  anexos?: { nombre: string; contentType: string; contenidoBase64: string }[];
}

/** Respuesta del endpoint de reintento de generación de acta */
export interface GenerarActaResponse {
  idActa360: number;
}

/**
 * Contacto adicional para notificaciones del cumplimiento.
 * Legacy: Tpsc_cumpli_contactosdto / TPSC_CUMPLI_CONTACTOS
 */
export interface ContactoAdicional {
  /** Nombre del contacto */
  dsnombre: string;
  
  /** Email del contacto */
  dsemail: string;
}

// === Response de Cumplimiento ===

/**
 * Response completo de un cumplimiento registrado.
 * 
 * Incluye datos del cumplimiento + información de la programación relacionada.
 * Legacy: CreateOrUpdateCumplimientoViewImpl.java (modo edición)
 */
export interface CumplimientoResponse {
  /** Tipo de cita de la programación del cumplimiento */
  nmconsecutivoTipoCita?: number;
  /** Radicado del informe registrado (legacy NM_RADICADO): abre la gestión de contactos del informe Final. */
  nmRadicadoInforme?: number;
  /** Valor efectivo del informe registrado: true si quedó Parcial (para clientes que no son la ARL siempre es Final). */
  informeParcial?: boolean;
  /** PK del cumplimiento */
  nmconsecutivo: number;
  
  /** Cantidad ejecutada */
  nmcantidadEjecutada: number;
  
  /** Número de asistentes */
  nmasistentes?: number;
  
  /** Nombre del contacto */
  dsnombreContacto?: string;
  
  /** Email del contacto */
  dsemailContacto?: string;
  
  // === Transporte ===
  
  /** Pago transporte empleado (S/N) */
  snpagoTransporteEmpleado?: string;
  
  /** Atención virtual (S/N) */
  snatencionVirtual?: string;
  
  /** Valor del transporte */
  ptvalorTransporte?: number;
  
  /** Municipio origen - código */
  cdmunicipioOrigen?: string;
  
  /** Municipio origen - nombre */
  dsmunicipioOrigen?: string;
  
  /** Municipio entrega - código */
  cdmunicipioEntrega?: string;
  
  /** Municipio entrega - nombre */
  dsmunicipioEntrega?: string;
  
  /** Código de población */
  cdpoblacion?: string;
  
  /** Nombre de población */
  dspoblacion?: string;
  
  /** Requiere transporte adicional (S/N) */
  snrequiereTransporteAdicional?: string;
  
  /** Valor transporte adicional */
  ptvalorTransporteAdicional?: number;
  
  /** Observaciones transporte adicional */
  dsobservacionesTransporteAdicional?: string;
  
  // === Justificación ===
  
  /** Código de justificación */
  cdjustificacion?: number;
  
  /** Descripción de justificación */
  dsjustificacion?: string;
  
  /** Observaciones de justificación */
  dsobservacionesJustificacion?: string;
  
  // === Información de la Programación (Solo lectura) ===
  
  /** Número de orden */
  nmconsecutivoManualOrden?: string;
  
  /** Tipo de servicio */
  dstipo?: string;
  
  /** Tipo de cita */
  dstipoCita?: string;
  
  /** Nombre del cliente */
  dsnombreCliente?: string;
  
  /** Nombre del beneficiario */
  dsnombreBeneficiario?: string;
  
  /** Consecutivo del beneficiario */
  nmconsecutivoBeneficiario?: number;
  
  /** Código de contrato del beneficiario */
  cdcontratoBeneficiario?: string;
  
  /** Estado de la programación (después de actualizar) */
  cdestado?: string;
  
  /** Descripción del estado */
  dsestado?: string;
  
  /** Nombre del servicio */
  dsservicio?: string;
  
  /** Nombre de la tarea */
  dstarea?: string;
  
  /** Fecha de programación */
  feprogramacion?: string;
  
  /** Cantidad programada */
  nmcantidadProgramada?: number;
  
  /** Saldo por ejecutar */
  saldoPorEjecutar?: number;

  // === Acta 360 (HU1132419) ===

  /** true cuando tipoCita=CAMPO y contrato NO es exento -> mostrar panel acta */
  asociarActa?: boolean;

  /** Contratos exentos separados por coma (para que el front valide) */
  contratosExentos?: string;

  /** Clasificación del servicio (ASESOR... | CAPACITACI...) */
  dsclasificacion?: string;

  /** ID del acta generada en sistema 360 (null si falló la generación) */
  idActa360?: number | null;

  /** Mensaje real del servicio 360 cuando la generación del acta falló en el guardado inicial */
  actaMensajeError?: string | null;

  /** true cuando la sincronizacion Outlook fallo sin bloquear el guardado */
  warningOutlook?: boolean;

  /** Mensaje funcional de warning de sincronizacion Outlook */
  mensajeWarningOutlook?: string | null;

  /** true cuando hubo warning no bloqueante al guardar observaciones/recomendaciones/contactos */
  warningPostPersistencia?: boolean;

  /** Mensaje funcional de warning post-persistencia */
  mensajeWarningPostPersistencia?: string | null;
}

// === Entidad Cumplimiento ===

/**
 * Entidad Cumplimiento (para listados)
 */
export interface Cumplimiento {
  nmconsecutivo: number;
  nmconsecutivoProgramacion: number;
  fecumplimiento: string;
  nmcantidadEjecutada: number;
  cdestado: string;
  dsestado?: string;
}

// === Saldo de Orden ===

/**
 * Response del saldo de una orden de servicio.
 */
export interface SaldoResponse {
  nmconsecOrdenServDetalle: number;
  saldoPorEjecutar: number;
}

/**
 * Configuración funcional del dominio de Cumplimientos.
 *
 * Backend: GET /api/servicios/orientacionesProgramadas/configuracion
 */
export interface ConfiguracionCumplimientoResponse {
  /** Límite de días hábiles para cumplimiento tardío. */
  limitDaysProgram: number;
  /** Contratos excluidos de asociación automática de actas. */
  contratosCumpl: string[];
  /** Tope del valor de transporte adicional (MAX_TTE_ADIC_CMPL). Ausente si no está parametrizado. */
  maxTransporteAdicional?: number;
}

// === Envío de Informes (P8/FileNet) ===

/**
 * Request para enviar un informe con documento adjunto a P8.
 * 
 * Legacy: EnviarInformeActivity.java -> prepara y envía documento a FileNet
 * Backend: InformeCumplimientoController POST /enviar (multipart)
 */
export interface EnvioInformeRequest {
  /** Archivo del informe en Base64 */
  archivoContenido: string;
  
  /** Nombre del archivo con extensión */
  archivoNombre: string;
  
  /** Content-Type del archivo (ej: application/pdf) */
  archivoContentType: string;
  
  /** Tipo de informe: P=Parcial, F=Final */
  tipoInforme: 'P' | 'F';
  
  /** Lista de nmconsecutivo de cumplimientos a incluir en el informe */
  informes: number[];
  
  /** DNI del usuario que envía */
  dniUsuario: string;
}

/**
 * Resultado del envío de informe a P8.
 * 
 * Contiene el número de radicado generado y el ID del documento en P8.
 */
export interface EnvioInformeResultado {
  /** Indica si el envío fue exitoso */
  exito: boolean;
  
  /** Mensaje descriptivo (éxito o error) */
  mensaje: string;
  
  /** Número de radicado generado por el sistema */
  nmRadicado?: string;
  
  /** ID del documento en P8/FileNet */
  idDocumentoP8?: string;
}

/**
 * Informe pendiente de envío (para listado).
 * 
 * Muestra los cumplimientos que aún no tienen documento P8 asociado.
 */
export interface InformePendiente {
  /** Consecutivo del cumplimiento */
  nmconsecutivo: number;
  
  /** Número de orden */
  nmconsecutivoManualOrden?: string;
  
  /** Nombre del beneficiario */
  dsnombreBeneficiario?: string;
  
  /** Nombre del servicio */
  dsservicio?: string;
  
  /** Fecha del cumplimiento */
  fecumplimiento?: string;
  
  /** Ya tiene documento P8 asociado */
  tieneDocumentoP8: boolean;
  
  /** ID del documento P8 (si existe) */
  idDocumentoP8?: string;
}

// === Historial de Cumplimientos (Modal) ===

/**
 * Cumplimiento para el modal "Lista de Cumplimientos".
 * 
 * Legacy: ListarCumplimientosViewImpl.java -> stCumplimientos (tabla)
 * 
 * Columnas del modal:
 * 1. Fecha Cumplimiento (feprogramacion)
 * 2. Cantidad Ejecutada (nmcantidadEjecutada)
 * 3. Núm. Asistentes (nmasistentes)
 * 4. Nombre Contacto (dsnombreContacto)
 * 5. Email Contacto (dsemailContacto)
 * 6. Transporte Empleado (ptvalorTransporte)
 * 7. Transporte Adicional (ptvalorTransporteAdicional)
 * 8. Estado Acta (calculado con validacionActa())
 */
export interface HistorialCumplimiento {
  /** PK del cumplimiento */
  nmconsecutivo: number;
  
  /** FK a la programación */
  nmconsecutivoProgramacion: number;
  
  /** FK al beneficiario */
  nmconsecutivoBeneficiario?: number;
  
  // === Datos del Cumplimiento (Columnas del modal) ===
  
  /** 
   * Fecha de la programación (columna "Fecha Cumplimiento")
   * Legacy: nmconsecutivo_programacion.feprogramacion
   */
  feprogramacion?: string;
  
  /** 
   * Cantidad ejecutada en este cumplimiento
   * Legacy: nmcantidadEjecutada
   */
  nmcantidadEjecutada?: number;
  
  /** 
   * Número de asistentes
   * Legacy: nmasistentes
   */
  nmasistentes?: number;
  
  /** 
   * Nombre del contacto
   * Legacy: ds_nombre_contacto
   */
  dsnombreContacto?: string;
  
  /** 
   * Email del contacto
   * Legacy: ds_email_contacto
   */
  dsemailContacto?: string;
  
  /** 
   * Valor del transporte empleado
   * Legacy: ptvalorTransporte
   */
  ptvalorTransporte?: number;
  
  /** 
   * Valor del transporte adicional
   * Legacy: ptvalorTransporteAdicional
   */
  ptvalorTransporteAdicional?: number;
  
  // === Campos para lógica validacionActa() ===
  
  /** 
   * Tipo de cita de la programación (para validar si es CAMPO = 17)
   * Legacy: TiposDeCita.CAMPO
   */
  nmconsecutivoTipoCita?: number;
  
  /** 
   * Contrato del beneficiario (para validar CONTRATOS_CUMPL)
   * Legacy: nmconsecutivo_beneficiario.cdcontrato
   */
  cdcontrato?: string;
  
  /**
   * Clasificación del servicio (ej: 'ASESOR...', 'CAPACITACI...')
   * Legacy: TPSC_HOMOLOGACIONES.CDCLASIFICACION vía JOIN
   * Necesario para determinar tipo de acta en modo soloActa.
   */
  dsclasificacion?: string;
  
  /** 
   * Consecutivo del acta asociada (de TGFC_CUMPLIM_ACTA)
   * Legacy: cdacta.cdconsecutivo_acta - Para descargar PDF
   */
  cdconsecutivoActa?: string;
  
  /** 
   * Estado del acta (de TGFC_CUMPLIM_ACTA.DSESTADO)
   * Legacy: validacionActa() usa este valor o retorna "SIN ASOCIAR ACTA"
   */
  dsestadoActa?: string;
  
  /** Fecha de ingreso (para ordenar) */
  feingresa?: string;
}

/**
 * Constantes para el tipo de cita CAMPO.
 * Legacy: TiposDeCita.CAMPO = 17
 */
export const TIPO_CITA_CAMPO = 17;

/**
 * Respuesta combinada de observación y recomendación.
 */
export interface ObservacionRecomendacion {
  observacion: string;
  recomendacion: string;
}

/**
 * Acta de cumplimiento (TGFC_CUMPLIM_ACTA).
 * Legacy: Tgfc_cumplim_actadto
 */
export interface ActaCumplimiento {
  nmcumplimientoActaId: number;
  dsfuente: string;
  cdconsecutivoCumplimiento: string;
  cdconsecutivoActa: string;
  cdidComCcm: string | null;
  cdidP8Preliminar: string | null;
  cdidP8Firmada: string | null;
  cdidDocVidaFirma: string | null;
  feenvio: string | null;
  fefirma: string | null;
  feingresa: string | null;
  femodifica: string | null;
  febaja: string | null;
  dniIngresa: string | null;
  dniModifica: string | null;
  feaprobacionAuto: string | null;
  dsestado: string;
  dserror: string | null;
}

/**
 * Respuesta del endpoint de asociar acta.
 */
export interface AsociarActaResponse {
  exito: boolean;
  mensaje: string;
}

